import type {
  ContactVerificationRequest,
  ManualActivityTriggerResponse,
  PreferenceUpdateRequest,
  PreferencesResponse,
  ProcessingAttemptsResponse,
  PublicStatusResponse,
  RecentActivitiesResponse,
  StravaAuthorizationStartResponse,
  TelegramLinkStartResponse
} from "../types";

const API_BASE = "/api";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {})
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = payload.message ?? "Request failed";
    throw new Error(message);
  }
  return payload as T;
}

export const api = {
  getPublicStatus(): Promise<PublicStatusResponse> {
    return request<PublicStatusResponse>("/status");
  },
  startStravaAuthorization(): Promise<StravaAuthorizationStartResponse> {
    return request<StravaAuthorizationStartResponse>("/oauth/strava/start");
  },
  getPreferences(): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/preferences");
  },
  updatePreferences(body: PreferenceUpdateRequest): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/preferences", {
      method: "PUT",
      body: JSON.stringify(body)
    });
  },
  acceptPrivacyDisclosure(ageEligibilityConfirmed: boolean): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/privacy-disclosure", {
      method: "POST",
      body: JSON.stringify({ age_eligibility_confirmed: ageEligibilityConfirmed })
    });
  },
  revokePrivacyDisclosure(): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/privacy-disclosure", { method: "DELETE" });
  },
  listRecentActivities(): Promise<RecentActivitiesResponse> {
    return request<RecentActivitiesResponse>("/activities/recent");
  },
  listProcessingAttempts(): Promise<ProcessingAttemptsResponse> {
    return request<ProcessingAttemptsResponse>("/activities/attempts");
  },
  triggerActivity(
    activityId: string,
    source: "recent" | "manual"
  ): Promise<ManualActivityTriggerResponse> {
    return request<ManualActivityTriggerResponse>("/activities/trigger", {
      method: "POST",
      body: JSON.stringify({ activity_id: activityId, source })
    });
  },
  updateContact(channel: string, destination: string): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/contact", {
      method: "PUT",
      body: JSON.stringify({ channel, destination })
    });
  },
  startTelegramLink(): Promise<TelegramLinkStartResponse> {
    return request<TelegramLinkStartResponse>("/contact/telegram/link/start", {
      method: "POST"
    });
  },
  confirmContact(body: ContactVerificationRequest): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/contact/confirm", {
      method: "POST",
      body: JSON.stringify(body)
    });
  },
  deleteContact(): Promise<PreferencesResponse> {
    return request<PreferencesResponse>("/contact", { method: "DELETE" });
  },
  unlink(): Promise<{ status: string }> {
    return request<{ status: string }>("/unlink", { method: "POST" });
  },
  joinWaitlist(emailAddress: string): Promise<{ status: string; source: string }> {
    return request<{ status: string; source: string }>("/waitlist", {
      method: "POST",
      body: JSON.stringify({ email_address: emailAddress, source: "capacity_full" })
    });
  }
};
